/**
 * @file weight.ts
 * @module app/modules/orders-delivery/utils/weight
 * @description Shared order weight display helper for the Orders Delivery module.
 * Returns a span node with a consistent label and title for total or processed weight.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.0
 * @version 2.1.2
 */

import React from 'react';
import { OrderWeightLike } from '../types/order-metrics';
import {
  normalizeOrderMetric,
  subtractProcessedMetric,
  sumOrderMetricValues,
} from './order-metric-values';

/**
 * Sums line weights; returns null when a supplied value is not finite.
 * @param {OrderWeightLike} orderVal - The order weight object containing processed and original total weights.
 * @returns {number | null} The total weight, or null when unavailable.
 */
export const getTotalWeightByCalculatingQuantityWeights = (
  orderVal: OrderWeightLike
) => {
  const quantities = orderVal?.totalQuantities;
  if (quantities != null && !Array.isArray(quantities)) {
    return null;
  }
  return sumOrderMetricValues((quantities ?? []).map((item) => item?.weight));
};

export const getRemainingOrderWeight = (orderVal: OrderWeightLike) =>
  subtractProcessedMetric(
    getTotalWeightByCalculatingQuantityWeights(orderVal),
    orderVal?.mProcessedWeight,
  );

const createOrderWeightSpan = (
  className: string | undefined,
  title: string | undefined,
  children: string | number
) => {
  return React.createElement(
    'span',
    {
      className: `${className ?? ''} order-weight`,
      title,
    },
    children
  );
};

/**
 * Converts kilograms to tons, formatting the result to three decimal places.
 * @param {number} kilograms - The weight in kilograms to convert.
 * @returns {string} The weight in tons, formatted to three decimal places.
 */
const defaultKgToTons = (kilograms: number) => {
  const conversionFactor = 0.001;
  const weight = normalizeOrderMetric(kilograms);
  if (weight === null) {
    return '-';
  }
  const tons = weight * conversionFactor;
  return tons.toFixed(3);
};

/**
 * Returns a span node using the original total-weight logic by summing totalQuantities.weight.
 * @param {OrderWeightLike} orderVal - The order weight object containing total quantities.
 * @param {string} [className] - Optional additional class name for the span element.
 * @param {(value: number) => string} [formatWeight=defaultKgToTons] - Weight formatter, typically kgToTons.
 * @returns {React.ReactElement} A React span element displaying the summed weight.
 */
export const getOrderWeightByCalculatingQuantityWeightsDisplay = (
  orderVal: OrderWeightLike,
  className?: string,
  formatWeight: (value: number) => string = defaultKgToTons
) => {
  const totalWeight = getTotalWeightByCalculatingQuantityWeights(orderVal);
  const formattedWeight = totalWeight === null ? '-' : formatWeight(totalWeight);

  return createOrderWeightSpan(
    className,
    totalWeight === null ? 'Order weight unavailable' : `total order ${formattedWeight} Ton`,
    `${formattedWeight} T`
  );
};